import { Switch, Route, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { useLocation } from "wouter";
import { trackPageView } from "@/lib/analytics";

const LazyTourProvider = lazy(() => import("@/components/product-tour").then(m => ({ default: m.TourProvider })));

function ConditionalTourProvider({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const isAdminRoute = location.startsWith('/admin');
  
  if (isAdminRoute) {
    return (
      <Suspense fallback={<>{children}</>}>
        <LazyTourProvider>{children}</LazyTourProvider>
      </Suspense>
    );
  }
  
  return <>{children}</>;
}
function useScrollRestoration() {
  const [location] = useLocation();
  const prevPathRef = useRef<string>("");

  useEffect(() => {
    const currentPath = location.split("#")[0];
    const prevPath = prevPathRef.current;
    const hasHash = window.location.hash.length > 0;

    if (currentPath !== prevPath && !hasHash) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }

    prevPathRef.current = currentPath;
  }, [location]);
}
import { ProtectedRoute } from "@/components/protected-route";

import NotFound from "@/pages/not-found";

const Home = lazy(() => import("@/pages/home"));
const Services = lazy(() => import("@/pages/Services"));
const AboutUs = lazy(() => import("@/pages/AboutUs"));
const Promotions = lazy(() => import("@/pages/Promotions"));
const Reviews = lazy(() => import("@/pages/Reviews"));
const Contact = lazy(() => import("@/pages/contact"));
const Blog = lazy(() => import("@/pages/blog"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const PricingConfigurator = lazy(() => import("@/pages/pricing-configurator"));
const BlogPost = lazy(() => import("@/pages/blog-post"));
const ThankYou = lazy(() => import("@/pages/thank-you"));
const Terms = lazy(() => import("@/pages/TermsOfService"));
const Privacy = lazy(() => import("@/pages/PrivacyPolicy"));
const Sitemap = lazy(() => import("@/pages/Sitemap"));
const QuickMessage = lazy(() => import("@/pages/QuickMessage"));
const Disclaimer = lazy(() => import("@/pages/disclaimer"));
const Accessibility = lazy(() => import("@/pages/accessibility"));
const SignContract = lazy(() => import("@/pages/sign-contract"));
const PayInvoice = lazy(() => import("@/pages/pay-invoice"));
const SetupPaymentPlan = lazy(() => import("@/pages/setup-payment-plan"));
const SetupSubscription = lazy(() => import("@/pages/setup-subscription"));

const AiAgents = lazy(() => import("@/pages/ai-agents"));
const WebDevelopment = lazy(() => import("@/pages/web-development"));
const Voice = lazy(() => import("@/pages/voice"));
const LpWebsites = lazy(() => import("@/pages/lp-websites"));
const LpVoice = lazy(() => import("@/pages/lp-voice"));
const LpConsulting = lazy(() => import("@/pages/lp-consulting"));

const ServiceLandingPage = lazy(() => import("@/pages/services/ServiceLandingPage"));
const ServicesDirectory = lazy(() => import("@/pages/services-directory"));
const Projects = lazy(() => import("@/pages/projects"));
const CaseStudyPage = lazy(() => import("@/pages/case-studies/CaseStudyPage"));

const AutomaticClimateHVAC = lazy(() => import("@/pages/case-studies/AutomaticClimateHVAC"));
const DrGarageDoor = lazy(() => import("@/pages/case-studies/DrGarageDoor"));
const TexasUniqueDesignRemodeling = lazy(() => import("@/pages/case-studies/TexasUniqueDesignRemodeling"));

const AdminLogin = lazy(() => import("@/pages/admin-login"));
const AdminDashboard = lazy(() => import("@/pages/admin-dashboard"));
const AdminTestimonials = lazy(() => import("@/pages/admin-testimonials"));
const AdminBlog = lazy(() => import("@/pages/admin-blog"));
const AdminAIGenerator = lazy(() => import("@/pages/admin-ai-generator"));
const AdminArticleQueue = lazy(() => import("@/pages/admin-article-queue"));
const AdminTopicBucket = lazy(() => import("@/pages/admin-topic-bucket"));
const AdminPages = lazy(() => import("@/pages/admin-pages"));
const AdminMedia = lazy(() => import("@/pages/admin-media"));
const AdminInquiries = lazy(() => import("@/pages/admin-inquiries"));
const AdminUsers = lazy(() => import("@/pages/admin-users"));
const AdminLandingPages = lazy(() => import("@/pages/admin-landing-pages"));
const AdminAnalytics = lazy(() => import("@/pages/admin-analytics"));
const AdminSearchConsole = lazy(() => import("@/pages/admin-search-console"));
const AdminSeo = lazy(() => import("@/pages/admin-seo"));
const AdminNexusClients = lazy(() => import("@/pages/admin-nexus-clients"));
const AdminNexusClientDetail = lazy(() => import("@/pages/admin-nexus-client-detail"));
const PortalLogin = lazy(() => import("@/pages/portal-login"));
const PortalDashboard = lazy(() => import("@/pages/portal-dashboard"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
      <div className="relative">
        <div className="w-16 h-16 border-4 border-[#00f0ff]/20 border-t-[#00f0ff] rounded-full animate-spin"></div>
        <div className="absolute inset-0 w-16 h-16 border-4 border-[#b366ff]/20 border-b-[#b366ff] rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
      </div>
    </div>
  );
}

function Router() {
  const [location] = useLocation();

  useScrollRestoration();

  useEffect(() => {
    trackPageView(location);
  }, [location]);

  return (
    <Switch>
      <Route path="/">
        <Suspense fallback={<PageLoader />}><Home /></Suspense>
      </Route>
      <Route path="/ai-agents">
        <Suspense fallback={<PageLoader />}><AiAgents /></Suspense>
      </Route>
      <Route path="/voice">
        <Suspense fallback={<PageLoader />}><Voice /></Suspense>
      </Route>
      <Route path="/lp/websites">
        <Suspense fallback={<PageLoader />}><LpWebsites /></Suspense>
      </Route>
      <Route path="/lp/voice">
        <Suspense fallback={<PageLoader />}><LpVoice /></Suspense>
      </Route>
      <Route path="/lp/consulting">
        <Suspense fallback={<PageLoader />}><LpConsulting /></Suspense>
      </Route>
      <Route path="/web-development">
        <Suspense fallback={<PageLoader />}><WebDevelopment /></Suspense>
      </Route>
      <Route path="/services">
        <Redirect to="/ai-agents" />
      </Route>
      <Route path="/about">
        <Suspense fallback={<PageLoader />}><AboutUs /></Suspense>
      </Route>
      <Route path="/promotions">
        <Suspense fallback={<PageLoader />}><Promotions /></Suspense>
      </Route>
      <Route path="/reviews">
        <Suspense fallback={<PageLoader />}><Reviews /></Suspense>
      </Route>
      <Route path="/pricing">
        <Suspense fallback={<PageLoader />}><Pricing /></Suspense>
      </Route>
      <Route path="/pricing/configure">
        <Suspense fallback={<PageLoader />}><PricingConfigurator /></Suspense>
      </Route>
      <Route path="/contact">
        <Suspense fallback={<PageLoader />}><Contact /></Suspense>
      </Route>
      <Route path="/blog">
        <Suspense fallback={<PageLoader />}><Blog /></Suspense>
      </Route>
      <Route path="/blog/:slug">
        <Suspense fallback={<PageLoader />}><BlogPost /></Suspense>
      </Route>

      {/* Projects Hub */}
      <Route path="/projects">
        <Suspense fallback={<PageLoader />}><Projects /></Suspense>
      </Route>

      {/* Case Studies - Specific pages first, then generic fallback */}
      <Route path="/case-studies/automatic-climate-hvac">
        <Suspense fallback={<PageLoader />}><AutomaticClimateHVAC /></Suspense>
      </Route>
      <Route path="/case-studies/dr-garage-door">
        <Suspense fallback={<PageLoader />}><DrGarageDoor /></Suspense>
      </Route>
      <Route path="/case-studies/texas-unique-design-remodeling">
        <Suspense fallback={<PageLoader />}><TexasUniqueDesignRemodeling /></Suspense>
      </Route>
      <Route path="/case-studies/:slug">
        <Suspense fallback={<PageLoader />}><CaseStudyPage /></Suspense>
      </Route>

      {/* Services Directory - must be before :slug route */}
      <Route path="/services/directory">
        <Suspense fallback={<PageLoader />}><ServicesDirectory /></Suspense>
      </Route>
      
      {/* Service Landing Pages */}
      <Route path="/services/:slug">
        <Suspense fallback={<PageLoader />}><ServiceLandingPage /></Suspense>
      </Route>
      
      {/* Admin routes */}
      <Route path="/admin/login">
        <Suspense fallback={<PageLoader />}><AdminLogin /></Suspense>
      </Route>
      <Route path="/admin">
        <Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense>
      </Route>
      <Route path="/admin/testimonials">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminTestimonials /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/blog">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminBlog /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/ai-generator">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminAIGenerator /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/article-queue">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminArticleQueue /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/topic-bucket">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminTopicBucket /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/pages">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminPages /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/media">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminMedia /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/leads">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminInquiries /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/users">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminUsers /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/landing-pages">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminLandingPages /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/analytics">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminAnalytics /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/search-console">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminSearchConsole /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/seo">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminSeo /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/nexus">
        <Redirect to="/admin/nexus/clients" />
      </Route>
      <Route path="/admin/nexus/clients">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminNexusClients /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/nexus/clients/:id">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminNexusClientDetail /></Suspense></ProtectedRoute>
      </Route>

      {/* Client Portal routes */}
      <Route path="/portal/login">
        <Suspense fallback={<PageLoader />}><PortalLogin /></Suspense>
      </Route>
      <Route path="/portal">
        <Suspense fallback={<PageLoader />}><PortalDashboard /></Suspense>
      </Route>

      {/* Utility pages */}
      <Route path="/thank-you">
        <Suspense fallback={<PageLoader />}><ThankYou /></Suspense>
      </Route>
      <Route path="/sign-contract/:token">
        <Suspense fallback={<PageLoader />}><SignContract /></Suspense>
      </Route>
      <Route path="/pay-invoice/:token">
        <Suspense fallback={<PageLoader />}><PayInvoice /></Suspense>
      </Route>
      <Route path="/setup-payment-plan/:token">
        <Suspense fallback={<PageLoader />}><SetupPaymentPlan /></Suspense>
      </Route>
      <Route path="/setup-subscription/:token">
        <Suspense fallback={<PageLoader />}><SetupSubscription /></Suspense>
      </Route>
      <Route path="/terms-of-service">
        <Suspense fallback={<PageLoader />}><Terms /></Suspense>
      </Route>
      <Route path="/terms">
        <Suspense fallback={<PageLoader />}><Terms /></Suspense>
      </Route>
      <Route path="/privacy-policy">
        <Suspense fallback={<PageLoader />}><Privacy /></Suspense>
      </Route>
      <Route path="/privacy">
        <Suspense fallback={<PageLoader />}><Privacy /></Suspense>
      </Route>
      <Route path="/sitemap">
        <Suspense fallback={<PageLoader />}><Sitemap /></Suspense>
      </Route>
      <Route path="/quick-message">
        <Suspense fallback={<PageLoader />}><QuickMessage /></Suspense>
      </Route>
      <Route path="/disclaimer">
        <Suspense fallback={<PageLoader />}><Disclaimer /></Suspense>
      </Route>
      <Route path="/accessibility">
        <Suspense fallback={<PageLoader />}><Accessibility /></Suspense>
      </Route>

      <Route component={NotFound} />
    </Switch>
  );
}

function DeferredToaster() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return mounted ? <Toaster /> : null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ConditionalTourProvider>
          <DeferredToaster />
          <Router />
        </ConditionalTourProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;